import { Company } from '@awork/features/company/models/company.model'
import { ProjectType } from '@awork/features/project/models/project-type.model'
import { ProjectStatus } from '@awork/features/project/models/project-status.model'
import { ProjectMember } from '@awork/features/project/models/project-member.model'
import type { User } from '@awork/features/user/models/user.model'
import { ProjectMilestone } from '@awork/features/project/models/project-milestone.model'
import type { IntegrationProviders } from '@awork/_shared/models/integration.model'
import type { Tag } from '@awork/_shared/models/tag.model'
import { Team } from '@awork/features/team/models/team.model'
import { getPixelRatio } from '@awork/_shared/functions/browser-infos'
import { apiEndpoint, isLiveMobile } from '@awork/environments/environment'
import { AccountQuery } from '@awork/_shared/state/account.query'
import type { ProjectQuery } from '@awork/features/project/state/project.query'
import type { CompanyQuery } from '@awork/features/company/state/company.query'
import type { IProjectBase } from '@awork/features/project/models/project.model.base'
import { ProjectBase } from '@awork/features/project/models/project.model.base'
import type { DeprecatedColor } from '@awork/_shared/types/color'
import { Color } from '@awork/_shared/types/color'
import { getMappedColor } from '@awork/_shared/functions/map-deprecated-colors'
import type { Workspace } from '@awork/features/workspace/models/workspace.model'
import { CustomFieldDefinitionLink } from '@awork/features/project/models/custom-field-definition-link.model'
import type { DynamicDurationPipe } from '@awork/_shared/pipes/dynamic-duration/dynamic-duration.pipe'
import { translate } from '@awork/_shared/functions/automation-translation-helper'
import { getLocalDateFromUTCString } from '@awork/_shared/functions/date-operations'
import type { CustomField } from '@awork/features/project/models/custom-field.model'
import type { ImageParams } from '@awork/_shared/services/file-service/types'

export interface ProjectConnectionInformation {
  homeWorkspaceId: string
  homeUserFullName: string
  homeProjectName: string
  homeWorkspaceName: string
  homeWorkspacePublicImageToken: string
  homeUserPublicImageToken: string

  // Partial entities with only the needed properties to display avatars
  homeUserAvatarEntity?: User
  homeWorkspaceAvatarEntity?: Workspace
}

export interface AcceptedProjectConnection extends ProjectConnectionInformation {
  projectId: string
  homeWorkspaceSubdomains?: string[]
}

export interface ProjectConnectionInvite {
  id: string
  projectId: string
  inviteCode: string
  isAccepted: boolean
  projectRoleId: string
  externalUserEmail: string
  externalWorkspaceId?: string
  externalUserId?: string
  externalUserFullName?: string
}

export interface ProjectConnectionStatus {
  isConnected: boolean
  isReadOnly?: boolean
}

export interface TrustedProjectConnection {
  id: string
  trustedWorkspaceId: string
  trustedWorkspaceName: string
  trustedWorkspaceSubdomain: string
}

export interface TrustedProjectConnectionForm {
  workspaceId?: string
  subdomain?: string
}

export enum AutopilotType {
  agile = 'agile',
  waterfall = 'waterfall'
}

export enum AutopilotChannel {
  msTeams = 'ms-teams',
  slack = 'slack',
  mail = 'mail'
}

export interface LinkedChat {
  channelName: string
  channelId: string
  provider: IntegrationProviders
}

export interface Autopilot {
  id: string
  projectId: string
  type: AutopilotType
  alerts: AutopilotAlert[]
  enabled: boolean
}

export interface AutopilotAlert {
  id: string
  autopilotId: string
  alert: string
  enabled: boolean
  channel: AutopilotChannel
}

export enum ProgressMode {
  time = 'progressByTime',
  tasks = 'progressByTasks',
  budget = 'progressByBudget'
}

export const ProjectColors = [
  Color.Red,
  Color.Orange,
  Color.Yellow,
  Color.Green,
  Color.Cyan,
  Color.Blue,
  Color.Indigo,
  Color.Purple
]

export type ProjectColor = (typeof ProjectColors)[number]

interface IProject {
  projectTypeId: string
  projectType: ProjectType
  projectTemplateId?: string
  publicProjectTemplateId?: string
  projectStatusId: string
  projectStatus: ProjectStatus
  companyId: string
  company: Partial<Company>
  salesType?: ProjectType
  updatedByUser: User
  imageLastUpdatedOn?: Date
  createdBy: string
  createdByUser: User
  members: ProjectMember[]
  description?: string
  tags: Tag[]
  teams?: Team[]
  trackedDuration: number
  resourceVersion: number
  tasksLoaded: boolean
  score?: number
  milestones?: ProjectMilestone[]
  pinned: boolean
  linkedChats?: LinkedChat[]
  showAsDisabled?: boolean
  tasksDoneCount?: number
  tasksCount?: number
  plannedDuration?: number
  totalPlannedDurationWithHierarchy?: number
  isBillableByDefault: boolean
  progressMode: ProgressMode
  modifiedOn?: number
  isPrivate?: boolean
  color?: ProjectColor | DeprecatedColor
  isExternal: boolean
  isMultiAssignmentAllowed?: boolean
  customFieldDefinitionLinks?: CustomFieldDefinitionLink[]
  isRetainer?: boolean
  deductNonBillableHours?: boolean
  customFields?: CustomField[]
  projectKey?: string
  workflowId?: string
  isProjectKeyVisible?: boolean | null
}

/**
 * The project in awork
 */
export class Project extends ProjectBase implements IProject {
  projectTypeId: string
  projectType: ProjectType
  projectTemplateId?: string
  publicProjectTemplateId?: string
  projectStatusId: string
  projectStatus: ProjectStatus
  companyId: string
  company: Company
  salesType?: ProjectType
  updatedByUser: User
  imageLastUpdatedOn?: Date
  createdBy: string
  createdByUser: User
  members: ProjectMember[]
  description?: string
  tags: Tag[]
  teams?: Team[]
  trackedDuration: number
  resourceVersion: number
  tasksLoaded: boolean
  score?: number // optional property to store the search score
  milestones?: ProjectMilestone[]
  pinned: boolean
  linkedChats?: LinkedChat[]
  showAsDisabled?: boolean
  tasksDoneCount?: number
  tasksCount?: number
  plannedDuration?: number
  totalPlannedDurationWithHierarchy?: number
  isBillableByDefault: boolean
  progressMode: ProgressMode
  modifiedOn?: number
  isPrivate?: boolean
  color: ProjectColor
  typeName = 'Project'
  isExternal: boolean
  isMultiAssignmentAllowed?: boolean
  customFieldDefinitionLinks?: CustomFieldDefinitionLink[]
  isRetainer?: boolean
  deductNonBillableHours?: boolean
  customFields?: CustomField[]
  projectKey?: string
  workflowId?: string
  isProjectKeyVisible?: boolean | null

  constructor(project: Partial<IProject & IProjectBase>) {
    super(project)

    if (!this.imageLastUpdatedOn && this.updatedOn) {
      this.imageLastUpdatedOn = new Date(this.updatedOn)
    } else if (typeof this.imageLastUpdatedOn === 'string') {
      this.imageLastUpdatedOn = new Date(this.imageLastUpdatedOn)
    }

    this.color = project.color ? getMappedColor(project.color) : undefined

    this.setProgressMode()
  }

  static isProject(project: unknown): project is Project {
    return (project as Project)?.typeName === 'Project'
  }

  get urlName(): string {
    return decodeURIComponent(this.name.replace(/ /g, '-'))
  }

  get nameAndClient(): string {
    if (this.company && this.company.name) {
      return `${this.name} (${this.company.name})`
    }

    return this.name
  }

  /**
   * Returns whether the given user is project creator.
   * @param {string | null | undefined} userId
   * @returns {boolean}
   */
  isCreator(userId: string | null | undefined): boolean {
    return !!userId && this.createdBy === userId
  }

  /**
   * Returns whether the given user is project member.
   * @param {string | null | undefined} userId
   * @param {ProjectMember[] | null | undefined} [members=this.members]
   * @returns {boolean}
   */
  isMember(userId: string | null | undefined, members: ProjectMember[] | null | undefined = this.members): boolean {
    if (!userId || !members?.length) {
      return false
    }

    return members.some(member => member.userId === userId)
  }

  /**
   * Returns whether the given user is either creator or member of the project.
   * @param {string | null | undefined} userId
   * @param {ProjectMember[] | null | undefined} [members=this.members]
   * @returns {boolean}
   */
  isCreatorOrMember(
    userId: string | null | undefined,
    members: ProjectMember[] | null | undefined = this.members
  ): boolean {
    return this.isCreator(userId) || this.isMember(userId, members)
  }

  /**
   * Returns whether the given member is the only member in the provided list.
   * @param {string | null | undefined} userId
   * @param {ProjectMember[] | null | undefined} [members=this.members]
   * @returns {boolean}
   */
  isOnlyMember(userId: string | null | undefined, members: ProjectMember[] | null | undefined = this.members): boolean {
    if (!userId || !members?.length) {
      return false
    }

    return members.length === 1 && members[0].userId === userId
  }

  get progressBudgetTextColor(): 'red' {
    if (!this.timeBudget) {
      return null
    }

    return this.trackedDuration / this.timeBudget > 1 ? 'red' : null
  }

  /**
   * Checks if the project is done.
   * Should track the "DoneTypes" list of the BE model.
   * @returns {boolean}
   */
  get isDone(): boolean {
    return this.projectStatus?.type === 'closed' || this.projectStatus?.type === 'stuck'
  }

  /**
   * Get text for progress tooltip
   * @param {{ progress: { time: number; tasks: number; budget: number; }; }} opts
   * @returns {string}
   */
  progressTooltipText(opts: {
    progress: {
      time: number
      tasks: number
      budget: number
    }
  }): string {
    const translation = q.translations.ProjectModel

    let currentProgress = ''

    switch (this.progressMode) {
      case ProgressMode.time:
        currentProgress = `${opts.progress.time}%`
        break
      case ProgressMode.tasks:
        currentProgress = `${opts.progress.tasks}%`
        break
      case ProgressMode.budget:
        currentProgress = `${opts.progress.budget}%`
        break
    }

    const currentProgressString = translate(translation[this.progressMode], {
      percentage: currentProgress
    })

    const nextProgress = this.nextProgressMode ? translation[this.nextProgressMode + 'Next'] : ''

    return nextProgress
      ? `<p class="aw-2-txt--center">${currentProgressString}<br/>${nextProgress}</p>`
      : currentProgressString
  }

  /**
   * Get the progress tooltip secondary text based on user settings
   * @param {DynamicDurationPipe} dynamicDurationPipe
   * @returns {string}
   */
  progressTooltipSecondaryText(dynamicDurationPipe: DynamicDurationPipe): string {
    const tracked = dynamicDurationPipe.transform(this.trackedDuration || 0)
    const planned = dynamicDurationPipe.transform(this.plannedDuration || 0)
    const budget = dynamicDurationPipe.transform(this.timeBudget || 0)

    switch (this.progressMode) {
      case ProgressMode.time:
        return `<i class="aw-2-icn aw-2-icn--s aw-2-txt--blue">timer</i> ${tracked} / ${planned}`
      case ProgressMode.tasks:
        return `<i class="aw-2-icn aw-2-icn--s aw-2-txt--green">done_all</i> ${this.tasksDoneCount} / ${this.tasksCount}`
      case ProgressMode.budget:
        return `<i class="aw-2-icn aw-2-icn--s aw-2-txt--blue">timer</i> ${tracked} / <i class="aw-2-icn aw-2-icn--s aw-2-txt--purple">timelapse</i> ${budget}`
    }
  }

  /**
   * Maps the project object and its nested objects
   * @param {Project} project The project to be mapped
   * @returns {Project}
   */
  static mapProject(project: Project): Project {
    project = new Project(project)

    if (project.projectType) {
      project.projectType = new ProjectType(project.projectType)
    } else {
      project.projectType = null
      project.projectTypeId = null
    }

    if (project.projectStatus) {
      project.projectStatus = new ProjectStatus(project.projectStatus)
    }

    if (project.company) {
      project.company = new Company(project.company)
    } else if (!project.companyId) {
      project.company = null
      project.companyId = null
    }

    if (project.salesType) {
      project.salesType = new ProjectType(project.salesType)
    }

    if (project.members) {
      project.members = project.members.map(member => new ProjectMember(member))
    }

    if (project.milestones) {
      project.milestones = project.milestones.map(milestone => new ProjectMilestone(milestone))
    }

    // Map array of strings to array of tags: Tag[].
    // To be removed later after the release and make sure all the stores are updated
    if (project.tags && isArrayOfStrings(project.tags)) {
      project.tags = mapTagsArr(project.tags as unknown as string[])
    }

    if (project.teams) {
      project.teams = project.teams.map(team => new Team(team))
    }

    if (project.dueDate) {
      project.dueDate = getLocalDateFromUTCString(project.dueDate)
    }

    if (project.startDate) {
      project.startDate = getLocalDateFromUTCString(project.startDate)
    }

    if (project.customFieldDefinitionLinks) {
      project.customFieldDefinitionLinks = project.customFieldDefinitionLinks.map(
        customFieldDefinitionLink => new CustomFieldDefinitionLink(customFieldDefinitionLink)
      )
    }

    return project
  }

  /**
   * Sets the progress mode according if project has time budget or planned duration
   * @param {boolean} isTimeTrackingDisabled
   */
  setProgressMode(isTimeTrackingDisabled?: boolean): void {
    if (isTimeTrackingDisabled) {
      this.progressMode = ProgressMode.tasks

      return
    }

    this.progressMode = this.isPrivate
      ? ProgressMode.tasks
      : this.timeBudget
        ? ProgressMode.budget
        : this.plannedDuration
          ? ProgressMode.time
          : ProgressMode.tasks
  }

  /**
   * Switches the progress mode between budget, time and tasks
   * Budget > Time > Task
   */
  setNextProgressMode(): void {
    if (this.nextProgressMode) {
      this.progressMode = this.nextProgressMode
    }
  }

  /**
   * Gets the next progress mode
   * @returns {ProgressMode}
   */
  get nextProgressMode(): ProgressMode {
    if (this.progressMode === ProgressMode.budget && this.plannedDuration) {
      return ProgressMode.time
    }

    if (this.progressMode === ProgressMode.tasks && this.timeBudget) {
      return ProgressMode.budget
    }

    if (this.progressMode === ProgressMode.tasks && this.plannedDuration) {
      return ProgressMode.time
    }

    if (this.progressMode !== ProgressMode.tasks) {
      return ProgressMode.tasks
    }

    return null
  }

  /**
   * Gets the project image according to the project data
   * @param {number} width
   * @param {number} height
   * @param {boolean} [addURLWrapper=true]
   * @param {ProjectQuery} projectQuery
   * @param {CompanyQuery} companyQuery
   * @returns {string}
   */
  getImage(
    width?: number,
    height?: number,
    addURLWrapper = true,
    projectQuery?: ProjectQuery,
    companyQuery?: CompanyQuery
  ): string {
    let image = null

    if (!!this.company && !(this.company instanceof Company)) {
      this.company = new Company(this.company)
    }

    if (this.hasImage) {
      image = this.profileImage(width, height, projectQuery)
    } else if (this.company?.hasImage) {
      image = this.company.getImage(width, height, companyQuery)
    }

    return image && addURLWrapper ? `url("${image}")` : image
  }

  /**
   * Returns the workspace image that belongs to the project
   * @returns {string}
   */
  getWorkspaceImage(): string {
    return `${apiEndpoint}/files/images/projects/${this.id}/workspace`
  }

  /**
   * Gets project profile image
   * @param {number} [width=500]
   * @param {number} [height=500]
   * @param {ProjectQuery} projectQuery
   * @returns {string}
   */
  profileImage(width = 500, height = 500, projectQuery?: ProjectQuery): string {
    // Get the project from the store to use the updatedOn in case this model does not have one
    if (projectQuery) {
      const project = projectQuery.getProject(this.id)

      if (project) {
        this.imageLastUpdatedOn = project.imageLastUpdatedOn
      }
    }

    const pixelRatio = getPixelRatio()
    let queryOptions: ImageParams = {
      crop: 'false',
      width: Math.floor(width * pixelRatio).toString(),
      height: Math.floor(height * pixelRatio).toString(),
      v: this.imageLastUpdatedOn?.getTime?.().toString()
    }

    // To fetch the images also locally
    if (isLiveMobile && AccountQuery.instance) {
      const accountState = AccountQuery.instance.getAccount()

      if (accountState) {
        const accessToken = accountState.accessToken as string

        queryOptions = {
          ...queryOptions,
          jwt: accessToken,
          'aw-mobile': 'true'
        }
      }
    }

    const url = `${apiEndpoint}/files/images/projects/${this.id}?${new URLSearchParams(queryOptions).toString()}`

    return url
  }
}

export function getProjectPropertyNames(): object {
  return {
    plannedduration: q.translations.ActivityLogComponent.entityProperties.plannedDuration,
    projecttypeid: q.translations.ActivityLogComponent.entityProperties.projectTypeId,
    projectstatusid: q.translations.ActivityLogComponent.entityProperties.projectStatusId,
    companyid: q.translations.ActivityLogComponent.entityProperties.company,
    name: q.translations.ActivityLogComponent.entityProperties.name,
    description: q.translations.ActivityLogComponent.entityProperties.description,
    startdate: q.translations.ActivityLogComponent.entityProperties.startDate,
    duedate: q.translations.ActivityLogComponent.entityProperties.dueDate,
    tags: q.translations.ActivityLogComponent.entityProperties.tags,
    trackedduration: q.translations.ActivityLogComponent.entityProperties.trackedDuration,
    projectmember: q.translations.ActivityLogComponent.entityProperties.projectMember,
    timebudget: q.translations.ActivityLogComponent.entityProperties.timeBudget,
    projectkey: q.translations.ActivityLogComponent.entityProperties.projectKey,
    workflowid: q.translations.ActivityLogComponent.entityProperties.workflowId
  }
}

export function getProjectPropertyArticles(): Record<string, string> {
  return {
    project: q.translations.common.articles.das,
    plannedduration: q.translations.common.articles.den,
    projecttypeid: q.translations.common.articles.den,
    projectstatusid: q.translations.common.articles.den,
    companyid: q.translations.common.articles.den,
    name: q.translations.common.articles.den,
    description: q.translations.common.articles.die,
    startdate: q.translations.common.articles.das,
    duedate: q.translations.common.articles.die,
    tags: q.translations.common.articles.den,
    trackedduration: q.translations.common.articles.die,
    projectmember: q.translations.common.articles.das,
    projecttag: q.translations.common.articles.den,
    timebudget: q.translations.common.articles.das,
    projectkey: q.translations.common.articles.den,
    workflowid: q.translations.common.articles.den
  }
}

/**
 * Is Array of strings
 * @param {unknown[]} value
 * @returns {boolean}
 */
export function isArrayOfStrings(value: unknown[]): boolean {
  return value?.every?.(item => typeof item === 'string')
}

/**
 * Map tags from Array of strings to Tag[]
 * @param {string[]} tags
 * @returns {Tag[]}
 */
export function mapTagsArr(tags: string[]): Tag[] {
  return tags?.map(tag => ({ name: tag }))
}
